import { ReactNode, useCallback, useMemo, useState } from 'react';
import { clearStoredToken, getStoredToken } from '@/auth/localToken';
import { logoutServer } from '@/api/auth';
import { useProfile } from '@/hooks/useProfile';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
  useIsAuthenticated,
} from '@azure/msal-react';
import type { IPublicClientApplication } from '@azure/msal-browser';
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import { msalConfig, loginRequest } from './config';

// Instantiating PublicClientApplication requires window.crypto.subtle, which
// the browser only exposes on *secure contexts* (HTTPS or localhost). On plain
// HTTP dev deployments, MSAL throws `BrowserAuthError: crypto_nonexistent` at
// module load, preventing React from ever mounting.
//
// We disable MSAL entirely when:
//   • no VITE_AZURE_CLIENT_ID is configured (dev bypass mode), OR
//   • the page is served over an insecure origin (no crypto.subtle).
//
// In that mode, `<AuthProvider>` becomes a pass-through and `useAuth()`
// returns a static no-op user so pages that call it keep rendering.

const hasClientId = Boolean(msalConfig.auth.clientId);
const hasSecureContext =
  typeof window !== 'undefined' &&
  typeof window.crypto !== 'undefined' &&
  typeof window.crypto.subtle !== 'undefined';

export const msalEnabled = hasClientId && hasSecureContext;

let _msalInstance: IPublicClientApplication | null = null;
if (msalEnabled) {
  try {
    _msalInstance = new PublicClientApplication(msalConfig);
  } catch (err) {
    // Any runtime error in MSAL init should never take down the whole app.
    console.warn('[AuthProvider] MSAL disabled — init failed:', err);
    _msalInstance = null;
  }
}

export const msalInstance = _msalInstance;

export function AuthProvider({ children }: { children: ReactNode }) {
  if (!_msalInstance) {
    // No auth configured — render children directly.
    return <>{children}</>;
  }
  return <MsalProvider instance={_msalInstance}>{children}</MsalProvider>;
}

export function AuthenticatedContent({ children }: { children: ReactNode }) {
  if (!_msalInstance) return <>{children}</>;
  return <AuthenticatedTemplate>{children}</AuthenticatedTemplate>;
}

export function UnauthenticatedContent({ children }: { children: ReactNode }) {
  if (!_msalInstance) return null;
  return <UnauthenticatedTemplate>{children}</UnauthenticatedTemplate>;
}

// ---------------------------------------------------------------------------
// useAuth — one hook, two implementations, chosen at module load.
// We can't conditionally call hooks, so we assign the correct function once.
// ---------------------------------------------------------------------------

function initialsFrom(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const noopUseAuth = () => ({
  isAuthenticated: true,
  isLoading: false,
  account: null,
  user: {
    name: 'Developer',
    email: 'dev@local',
    initials: 'DV',
  } as { name: string; email: string; initials: string } | null,
  login: async () => {},
  logout: async () => {},
  getAccessToken: async (): Promise<string | null> => null,
});

function useAuthLocal() {
  const { data: config } = useAuthConfig();
  const enforced = config?.auth_enforced ?? false;
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const { data: profile, isLoading: profileLoading } = useProfile(enforced && Boolean(token));

  const user = useMemo(() => {
    if (profile) {
      return {
        name: profile.display_name,
        email: profile.email,
        initials: initialsFrom(profile.display_name),
        role: profile.role,
      };
    }
    if (token) {
      return { name: 'User', email: '', initials: 'U', role: 'user' };
    }
    return null;
  }, [profile, token]);

  const login = useCallback(async () => {
    window.location.href = '/login';
  }, []);

  const logout = useCallback(async () => {
    await logoutServer();
    clearStoredToken();
    setToken(null);
    window.location.href = '/login';
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => token, [token]);

  const bootstrapUser = {
    name: 'Developer',
    email: 'dev@local',
    initials: 'DV',
    role: 'admin',
  };

  return {
    isAuthenticated: enforced ? Boolean(token) : true,
    isLoading: enforced && profileLoading && Boolean(token),
    account: null,
    user: enforced ? user : bootstrapUser,
    login,
    logout,
    getAccessToken,
  };
}

function useAuthMsal() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (accounts.length === 0) return null;

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const response = await instance.acquireTokenRedirect(loginRequest);
          return (response as unknown as { accessToken: string })?.accessToken ?? null;
        } catch (redirectError) {
          console.error('Token acquisition failed:', redirectError);
          return null;
        }
      }
      console.error('Token acquisition failed:', error);
      return null;
    }
  }, [instance, accounts]);

  const account = accounts[0] ?? null;

  return {
    isAuthenticated,
    isLoading: inProgress !== InteractionStatus.None,
    account,
    user: account
      ? {
          name: account.name ?? 'Unknown',
          email: account.username ?? '',
          initials: (account.name ?? 'U')
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2),
        }
      : null,
    login,
    logout,
    getAccessToken,
  };
}

export const useAuth = _msalInstance ? useAuthMsal : useAuthLocal;
